/**
 * Schedule grid geometry.
 *
 * Kept out of the component so the arithmetic can be tested directly. The cases that
 * matter are the ones a calendar library gets wrong for radio: a day that is not 24 hours
 * long, a slot that crosses midnight, two slots that overlap, a slot shorter than its own
 * label, and a drag that has to land on a sensible minute.
 *
 * Everything here works in the station's timezone, never the viewer's. A scheduler drawn
 * against a laptop's clock puts a 20:00 show in the wrong row for anyone travelling, and
 * the row it draws disagrees with the time printed on the block.
 */

import { DAY_MS, zoneOffsetMs, zonedDayStart } from './clock.js';

export { DAY_MS, zoneOffsetMs, zonedDayStart };

export const HOUR_HEIGHT_PX = 56;

/** A block is never drawn shorter than this, or it cannot be clicked. */
export const MIN_BLOCK_MINUTES = 10;

export interface DayBounds {
  /** The instant of 00:00 station-local on this day. */
  start: Date;
  /** The instant of 00:00 station-local on the next day. 23 or 25 hours across a DST change. */
  end: Date;
}

export interface LayoutInput {
  id: string;
  startAt: string | Date;
  endAt: string | Date;
}

export interface LayoutResult {
  id: string;
  dayIndex: number;
  topPx: number;
  heightPx: number;
  /** True when the slot began before this day and continues into it. */
  continued: boolean;
  /** Which lane this block occupies where slots overlap, and how many lanes there are. */
  column: number;
  columns: number;
}

/**
 * Consecutive station-local days.
 *
 * Each day runs to the next local midnight rather than to a fixed 24 hours, so the day a
 * clock change falls on is 23 or 25 hours long and every block on it still lands on the
 * hour it actually airs.
 */
export function dayBoundsFrom(from: Date, days: number, timeZone: string): DayBounds[] {
  const bounds: DayBounds[] = [];
  let start = zonedDayStart(from, timeZone);

  for (let index = 0; index < days; index += 1) {
    // Stepping 26 hours and re-snapping clears both a 23 and a 25 hour day.
    const end = zonedDayStart(new Date(start.getTime() + 26 * 3_600_000), timeZone);
    bounds.push({ start, end });
    start = end;
  }

  return bounds;
}

/**
 * Place blocks onto a day-column grid.
 *
 * A slot that crosses midnight appears on both days rather than being clipped to the
 * first. Midnight Melodies runs 23:30 to 01:40, and a scheduler that hides the second
 * half of it is lying about the night.
 *
 * Overlapping slots are given side-by-side lanes. Stacking them would hide one behind the
 * other, which is exactly the case an operator most needs to see.
 */
export function layoutBlocks(
  blocks: readonly LayoutInput[],
  days: readonly DayBounds[],
): LayoutResult[] {
  const output: LayoutResult[] = [];

  for (const [dayIndex, day] of days.entries()) {
    const placed: LayoutResult[] = [];
    const spans: Array<{ start: number; end: number }> = [];

    for (const block of blocks) {
      const start = new Date(block.startAt);
      const end = new Date(block.endAt);
      if (end <= start) continue;
      if (end <= day.start || start >= day.end) continue;

      const visibleStart = start < day.start ? day.start : start;
      const visibleEnd = end > day.end ? day.end : end;

      const topMinutes = (visibleStart.getTime() - day.start.getTime()) / 60_000;
      const durationMinutes = Math.max(
        (visibleEnd.getTime() - visibleStart.getTime()) / 60_000,
        MIN_BLOCK_MINUTES,
      );

      placed.push({
        id: block.id,
        dayIndex,
        topPx: (topMinutes / 60) * HOUR_HEIGHT_PX,
        heightPx: (durationMinutes / 60) * HOUR_HEIGHT_PX,
        continued: start < day.start,
        column: 0,
        columns: 1,
      });
      spans.push({ start: topMinutes, end: topMinutes + durationMinutes });
    }

    assignLanes(placed, spans);
    output.push(...placed);
  }

  return output;
}

/**
 * Give every block a lane, so overlapping slots sit beside each other.
 *
 * Blocks are grouped into clusters of mutual overlap, and every block in a cluster is
 * drawn at the cluster's width. Two overlapping shows are then both readable, and the
 * fact that they overlap is visible from the shape rather than only from a marker.
 */
function assignLanes(placed: LayoutResult[], spans: Array<{ start: number; end: number }>): void {
  const order = placed
    .map((_, index) => index)
    .sort((a, b) => spans[a]!.start - spans[b]!.start || spans[a]!.end - spans[b]!.end);

  let cluster: number[] = [];
  let clusterEnd = -Infinity;

  const closeCluster = (): void => {
    const width = Math.max(1, ...cluster.map((index) => placed[index]!.column + 1));
    for (const index of cluster) placed[index]!.columns = width;
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const index of order) {
    const span = spans[index]!;

    if (span.start >= clusterEnd && cluster.length > 0) closeCluster();

    // The lowest lane not occupied by anything this block overlaps.
    const taken = new Set(
      cluster
        .filter((other) => spans[other]!.end > span.start)
        .map((other) => placed[other]!.column),
    );
    let lane = 0;
    while (taken.has(lane)) lane += 1;

    placed[index]!.column = lane;
    cluster.push(index);
    clusterEnd = Math.max(clusterEnd, span.end);
  }

  if (cluster.length > 0) closeCluster();
}

/**
 * Where a drag lands.
 *
 * Snapped to whole minutes because broadcast schedules are not planned to the second,
 * and clamped inside the day so a drop near the bottom edge cannot produce a start time
 * on the following day by accident.
 */
export function snapDrop(params: {
  day: DayBounds;
  /** Pointer position inside the column, in pixels from its top. */
  offsetPx: number;
  /** Where inside the block the pointer grabbed it, in minutes. */
  grabOffsetMinutes: number;
  snapMinutes: number;
}): Date {
  const dayMinutes = (params.day.end.getTime() - params.day.start.getTime()) / 60_000;
  const minutes = (params.offsetPx / HOUR_HEIGHT_PX) * 60 - params.grabOffsetMinutes;
  const snapped = Math.round(minutes / params.snapMinutes) * params.snapMinutes;
  const clamped = Math.max(0, Math.min(snapped, dayMinutes - 1));
  return new Date(params.day.start.getTime() + clamped * 60_000);
}

/** The now-line, or null when the current time is outside the visible range. */
export function nowPosition(
  now: Date,
  days: readonly DayBounds[],
): { dayIndex: number; topPx: number } | null {
  const index = days.findIndex((day) => now >= day.start && now < day.end);
  if (index < 0) return null;

  const minutes = (now.getTime() - days[index]!.start.getTime()) / 60_000;
  return { dayIndex: index, topPx: (minutes / 60) * HOUR_HEIGHT_PX };
}
